import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { STATUS_TONE_SURFACE } from './statusToneClasses.js';
import { useIsCompactTier } from './useIsCompactTier.js';

/**
 * Admin Button — local wrapper (EXC-G12).
 * Spec D8 sizes: sm=32 / md=40 / lg=48. Desktop default 40; compact primary → 48.
 * Loading preserves width. Icon-only requires `aria-label`.
 * See docs/governance/BUTTON_SIZE_NOTE.md.
 */
const button = tv({
  base: [
    'relative inline-flex items-center justify-center gap-2',
    'rounded-md font-semibold font-[inherit] leading-snug',
    'transition-[background,border-color,color,box-shadow,transform,filter]',
    'duration-[var(--motion-duration-button,100ms)]',
    'ease-[var(--motion-ease-standard,cubic-bezier(0.4,0,0.2,1))]',
    'focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-[var(--color-an-primary)]',
    'disabled:cursor-not-allowed disabled:opacity-55',
    'disabled:transform-none disabled:shadow-none',
  ].join(' '),
  variants: {
    intent: {
      primary: [
        /* Avoid commas inside text-[var(...)] — Tailwind splits on `,` and drops on-primary color. */
        'border-0 bg-[var(--color-an-primary)] text-[color:var(--color-an-on-primary)] shadow-sm',
        'hover:brightness-95 hover:-translate-y-px',
        'hover:shadow-[var(--elevation-3)]',
      ].join(' '),
      secondary: [
        'border border-[var(--color-gray-300)] bg-[var(--color-gray-200)]',
        'text-[var(--color-gray-800)] shadow-sm',
        'hover:border-[var(--color-an-primary)] hover:bg-[var(--color-an-primary-soft)]',
        'hover:text-[var(--color-an-primary)]',
        'hover:shadow-[var(--elevation-2)]',
      ].join(' '),
      ghost: [
        'border border-[var(--color-an-border)] bg-[var(--color-an-surface)]',
        'text-[var(--color-an-text)]',
        'hover:border-[var(--color-an-primary)] hover:bg-[var(--color-an-primary-soft)]',
        'hover:text-[var(--color-an-primary)]',
      ].join(' '),
      danger: [
        'border-0 bg-[var(--color-an-danger)] text-white shadow-sm',
        'hover:opacity-90',
      ].join(' '),
      subtleDanger: [
        /* Theme tokens — rose-* is light-only and unreadable on dark charcoal cards. */
        'border border-[color-mix(in_srgb,var(--color-an-danger)_40%,var(--color-an-border))]',
        'bg-[var(--color-an-danger-soft)] text-[var(--color-an-danger)] shadow-none',
        'hover:border-[var(--color-an-danger)] hover:bg-[color-mix(in_srgb,var(--color-an-danger)_22%,var(--color-an-danger-soft))]',
        'hover:text-[var(--color-an-danger)] hover:shadow-none',
        'focus-visible:outline-[var(--color-an-danger)]',
      ].join(' '),
      success: [
        /* Soft status-pill recipe — Badge / STATUS_TONE_SURFACE.success (not solid CTA fill). */
        STATUS_TONE_SURFACE.success,
        'border border-[color-mix(in_srgb,var(--color-an-success)_40%,var(--color-an-border))] shadow-none',
        'hover:border-[var(--color-an-success)] hover:bg-[color-mix(in_srgb,var(--color-an-success)_22%,var(--color-an-success-soft))]',
        'hover:text-[var(--color-an-success)] hover:shadow-none',
        'focus-visible:outline-[var(--color-an-success)]',
      ].join(' '),
    },
    size: {
      /* Spec D8: 32 / 40 / 48 */
      sm: 'h-8 min-h-8 px-3 text-xs',
      md: 'h-10 min-h-10 px-4 text-sm',
      lg: 'h-12 min-h-12 px-5 text-base',
    },
    block: {
      true: 'w-full',
      false: '',
    },
    iconOnly: {
      true: 'aspect-square px-0 min-h-[44px] min-w-[44px]',
      false: '',
    },
  },
  compoundVariants: [
    /* Spec D8: compact (<lg) primary default height 48 (auto min-height). */
    {
      intent: 'primary',
      iconOnly: false,
      class: 'max-lg:h-auto max-lg:min-h-12',
    },
  ],
  defaultVariants: {
    intent: 'primary',
    size: 'md',
    block: false,
    iconOnly: false,
  },
});

type ButtonVariants = VariantProps<typeof button>;

export type ButtonSize = NonNullable<ButtonVariants['size']>;
export type ButtonIntent = NonNullable<ButtonVariants['intent']>;

type ButtonBaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  readonly intent?: ButtonIntent;
  readonly size?: ButtonSize;
  readonly block?: boolean;
  /** Shows spinner; preserves width via invisible label (no collapse). */
  readonly loading?: boolean;
  readonly children?: ReactNode;
};

export type ButtonProps =
  | (ButtonBaseProps & {
      readonly iconOnly?: false;
      readonly 'aria-label'?: string;
    })
  | (ButtonBaseProps & {
      /** Square icon button — `aria-label` is required. */
      readonly iconOnly: true;
      readonly 'aria-label': string;
    });

function assertIconOnlyAccessibleName(
  iconOnly: boolean,
  ariaLabel: string | undefined,
): void {
  if (!iconOnly) {
    return;
  }
  if (typeof ariaLabel === 'string' && ariaLabel.trim().length > 0) {
    return;
  }
  throw new Error(
    'Button: iconOnly requires a non-empty aria-label for an accessible name. Add aria-label (e.g. "Close") and retry.',
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      intent,
      size,
      block,
      loading = false,
      iconOnly = false,
      children,
      disabled,
      type = 'button',
      'aria-label': ariaLabel,
      title,
      ...rest
    },
    ref,
  ) => {
    assertIconOnlyAccessibleName(iconOnly, ariaLabel);

    const isCompact = useIsCompactTier();
    const resolvedSize =
      size ?? (isCompact && (intent === 'primary' || intent === undefined) ? 'lg' : 'md');

    const resolvedAriaLabel =
      ariaLabel ??
      (loading && typeof children === 'string' ? children : undefined);
    const tooltipTitle = title ?? (iconOnly ? resolvedAriaLabel : undefined);

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        className={button({ intent, size: resolvedSize, block, iconOnly, className })}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={resolvedAriaLabel}
        title={tooltipTitle}
        data-icon-only={iconOnly ? 'true' : undefined}
        data-loading={loading ? 'true' : undefined}
        data-compact-primary={isCompact && resolvedSize === 'lg' && (intent === 'primary' || intent === undefined) ? 'true' : undefined}
      >
        {loading ? (
          <>
            <span className="invisible inline-flex items-center gap-2" aria-hidden="true">
              {children}
            </span>
            <span
              className="absolute inset-0 inline-flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </span>
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';
